<template>
  <div class="billing-container">
    <h2>Subscription Billing</h2>
    <p>Enter your payment details to start your 30-day free trial.</p>

    <form id="helcimForm" @submit.prevent="processPayment">
      <div class="form-group">
        <label for="cardNumber">Card Number</label>
        <input type="text" id="cardNumber" v-model="cardData.cardNumber" placeholder="0000 0000 0000 0000" required />
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="cardExpiry">Expiry (MM/YY)</label>
          <input type="text" id="cardExpiry" v-model="cardData.cardExpiry" placeholder="MM/YY" required />
        </div>
        <div class="form-group">
          <label for="cardCVV">CVV</label>
          <input type="text" id="cardCVV" v-model="cardData.cardCVV" placeholder="123" required />
        </div>
      </div>

      <div class="form-group">
        <label for="cardHolderName">Cardholder Name</label>
        <input type="text" id="cardHolderName" v-model="cardData.cardHolderName" placeholder="John Doe" required />
      </div>

      <button type="submit" :disabled="loading">
        {{ loading ? 'Processing...' : 'Start 30-Day Free Trial' }}
      </button>

      <p v-if="error" class="error-message">{{ error }}</p>
      <p v-if="success" class="success-message">Success! Your trial has started.</p>
    </form>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useRouter } from 'vue-router';

const INITIALIZE_SUBSCRIPTION = gql`
  mutation InitializeSubscription($helcimToken: String!) {
    InitializeSubscription(helcimToken: $helcimToken) {
      userID
      subscriptionStatus
      trialEndsAt
    }
  }
`;

export default {
  name: 'BillingView',
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const error = ref(null);
    const success = ref(false);
    const cardData = ref({
      cardNumber: '',
      cardExpiry: '',
      cardCVV: '',
      cardHolderName: ''
    });

    const { mutate: initializeSubscription } = useMutation(INITIALIZE_SUBSCRIPTION);

    const processPayment = async () => {
      loading.value = true;
      error.value = null;

      try {
        // 1. Use Helcim.js to tokenize the card
        // Note: This assumes Helcim.js is loaded in index.html
        if (typeof window.helcimProcess !== 'function') {
          throw new Error('Helcim library not loaded');
        }

        // Helcim.js V2 uses a specific way to tokenize
        // We need to provide the Account ID and a few other details
        const helcimConfig = {
          token: process.env.VUE_APP_HELCIM_JS_TOKEN, // This is your public JS token
          customerCode: 'USER-' + Date.now(), // Temporary code
        };

        // This is a simplified version of the Helcim.js call
        // In a real implementation, you'd use their specific tokenization function
        window.helcimProcess(helcimConfig, async (response) => {
          if (response.status === 'success') {
            const token = response.token;

            // 2. Send the token to our backend
            const result = await initializeSubscription({ helcimToken: token });
            
            if (result.data.InitializeSubscription) {
              success.value = true;
              // Redirect to dashboard after a short delay
              setTimeout(() => {
                router.push('/dashboard');
              }, 2000);
            }
          } else {
            error.value = response.message || 'Payment tokenization failed';
          }
          loading.value = false;
        });

      } catch (err) {
        console.error('Billing Error:', err);
        error.value = err.message || 'An unexpected error occurred';
        loading.value = false;
      }
    };

    return {
      cardData,
      loading,
      error,
      success,
      processPayment
    };
  }
};
</script>

<style scoped>
.billing-container {
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
}

.form-group {
  margin-bottom: 15px;
}

.form-row {
  display: flex;
  gap: 10px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

button {
  width: 100%;
  padding: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

button:disabled {
  background-color: #ccc;
}

.error-message {
  color: red;
  margin-top: 10px;
}

.success-message {
  color: green;
  margin-top: 10px;
}
</style>
